import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiService';
import type { Training, TrainingSectionExercise } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, Play, Pause, SkipForward, CheckCircle } from 'lucide-react';
import './RunTraining.css';

interface TrainingStep {
  type: 'exercise' | 'rest';
  section: 'warmup' | 'main' | 'cooldown';
  exercise?: TrainingSectionExercise;
  duration: number; // in seconds
  series?: number;
  currentSeries?: number;
  restType?: 'exercise' | 'series';
}

export const RunTraining: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [training, setTraining] = useState<Training | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [steps, setSteps] = useState<TrainingStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    loadTraining();
  }, [id]);

  useEffect(() => {
    if (training) {
      const generatedSteps = generateSteps(training);
      setSteps(generatedSteps);
      if (generatedSteps.length > 0) {
        setTimeRemaining(generatedSteps[0].duration);
      }
    }
  }, [training]);

  useEffect(() => {
    let interval: number | null = null;

    if (isRunning && timeRemaining > 0) {
      interval = window.setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleStepComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, timeRemaining]);

  const loadTraining = async () => {
    if (!id) return;

    try {
      const data = await apiService.getTraining(parseInt(id));
      setTraining(data);
    } catch (error) {
      console.error('Error loading training:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateSteps = (training: Training): TrainingStep[] => {
    const steps: TrainingStep[] = [];

    // Warmup section
    if (training.warmup_section?.exercises && training.warmup_section.exercises.length > 0) {
      training.warmup_section.exercises.forEach((exercise) => {
        const seconds = training.warmup_section!.params.seconds || 30;
        const rest = training.warmup_section!.params.rest_between_exercises || 0;

        steps.push({
          type: 'exercise',
          section: 'warmup',
          exercise,
          duration: seconds,
        });

        // Add rest after each exercise
        if (rest > 0) {
          steps.push({
            type: 'rest',
            section: 'warmup',
            duration: rest,
            restType: 'exercise',
          });
        }
      });
    }

    // Main section
    if (training.main_section?.exercises && training.main_section.exercises.length > 0) {
      const series = training.main_section.params.series || 1;
      const seconds = training.main_section.params.seconds || 30;
      const restBetweenSeries = training.main_section.params.rest_between_series || 0;
      const restBetweenExercises = training.main_section.params.rest_between_exercises || 0;

      // For each series, do all exercises
      for (let seriesNum = 1; seriesNum <= series; seriesNum++) {
        training.main_section.exercises.forEach((exercise, exerciseIndex) => {
          steps.push({
            type: 'exercise',
            section: 'main',
            exercise,
            duration: seconds,
            series,
            currentSeries: seriesNum,
          });

          // Add rest between exercises (except for last exercise in the series)
          if (restBetweenExercises > 0 && exerciseIndex < training.main_section!.exercises.length - 1) {
            steps.push({
              type: 'rest',
              section: 'main',
              duration: restBetweenExercises,
              restType: 'exercise',
            });
          }
        });

        // Add rest after each series
        if (restBetweenSeries > 0) {
          steps.push({
            type: 'rest',
            section: 'main',
            duration: restBetweenSeries,
            restType: 'series',
          });
        }
      }
    }

    // Cooldown section
    if (training.cooldown_section?.exercises && training.cooldown_section.exercises.length > 0) {
      training.cooldown_section.exercises.forEach((exercise) => {
        const seconds = training.cooldown_section!.params.seconds || 30;
        const rest = training.cooldown_section!.params.rest_between_exercises || 0;

        steps.push({
          type: 'exercise',
          section: 'cooldown',
          exercise,
          duration: seconds,
        });

        // Add rest after each exercise
        if (rest > 0) {
          steps.push({
            type: 'rest',
            section: 'cooldown',
            duration: rest,
            restType: 'exercise',
          });
        }
      });
    }

    return steps;
  };

  const handleStepComplete = () => {
    if (currentStepIndex < steps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      setTimeRemaining(steps[nextIndex].duration);
    } else {
      setIsRunning(false);
      setIsCompleted(true);
    }
  };

  const handlePlayPause = () => {
    setIsRunning(!isRunning);
  };

  const handleSkip = () => {
    handleStepComplete();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getSectionName = (section: string): string => {
    switch (section) {
      case 'warmup':
        return t('warmup');
      case 'main':
        return t('mainPart');
      case 'cooldown':
        return t('cooldown');
      default:
        return section;
    }
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  if (!training) {
    return <div className="text-center p-4">Training not found</div>;
  }

  const currentStep = steps[currentStepIndex];

  // Find next exercise step (skip rest steps)
  const getNextExercise = () => {
    for (let i = currentStepIndex + 1; i < steps.length; i++) {
      if (steps[i].type === 'exercise') {
        return steps[i];
      }
    }
    return null;
  };

  const nextExercise = currentStep?.type === 'rest' ? getNextExercise() : null;

  if (isCompleted) {
    return (
      <div className="run-training-page">
        <Card className="completion-card">
          <CheckCircle size={64} className="completion-icon" />
          <h1 className="completion-title">{t('trainingCompleted') || 'Training Completed!'}</h1>
          <p className="completion-message">{t('greatJob') || 'Great job! You have completed your training.'}</p>
          <Button onClick={() => navigate('/user/calendar')} className="completion-button">
            {t('backToCalendar') || 'Back to Calendar'}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="run-training-page">
      <div className="run-training-header">
        <Button variant="secondary" onClick={() => navigate('/user/calendar')} className="back-button">
          <ArrowLeft size={20} />
          {t('back')}
        </Button>
        <h1 className="run-training-title">{training.title}</h1>
      </div>

      <div className="run-training-container">
        <Card className="timer-card">
          <div className={`step-indicator ${currentStep?.type}`}>
            <span className="step-section">{getSectionName(currentStep?.section)}</span>
            <span className="step-counter">
              {currentStepIndex + 1} / {steps.length}
            </span>
          </div>

          {currentStep?.type === 'exercise' ? (
            <>
              <div className="exercise-display">
                <h2 className="exercise-name">{currentStep.exercise?.name}</h2>
                {currentStep.currentSeries && currentStep.series && (
                  <p className="series-info">
                    {t('series')} {currentStep.currentSeries} / {currentStep.series}
                  </p>
                )}
                {currentStep.exercise?.description && (
                  <p className="exercise-description">{currentStep.exercise.description}</p>
                )}
                {currentStep.exercise?.imageUrl && (
                  <div className="exercise-image-container">
                    <img
                      src={currentStep.exercise.imageUrl}
                      alt={currentStep.exercise.name}
                      className="exercise-image"
                    />
                  </div>
                )}
              </div>

              <div className="timer-display">
                <div className="timer-circle">
                  <svg className="timer-svg" viewBox="0 0 200 200">
                    <circle
                      className="timer-circle-bg"
                      cx="100"
                      cy="100"
                      r="90"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="10"
                    />
                    <circle
                      className="timer-circle-progress"
                      cx="100"
                      cy="100"
                      r="90"
                      fill="none"
                      stroke={currentStep?.type === 'exercise' ? 'var(--primary)' : '#10b981'}
                      strokeWidth="10"
                      strokeDasharray={`${2 * Math.PI * 90}`}
                      strokeDashoffset={`${2 * Math.PI * 90 * (1 - timeRemaining / currentStep?.duration)}`}
                      strokeLinecap="round"
                      transform="rotate(-90 100 100)"
                    />
                  </svg>
                  <div className="timer-text">
                    <span className="timer-value">{formatTime(timeRemaining)}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rest-layout">
              <div className="rest-left-column">
                <div className="rest-info">
                  <h2 className="rest-title">{t('rest') || 'Rest'}</h2>
                  <p className="rest-message">
                    {currentStep?.restType === 'series'
                      ? t('restBetweenSeries') || 'Rest between series'
                      : t('restBetweenExercises') || 'Rest before next exercise'}
                  </p>
                </div>

                <div className="timer-display">
                  <div className="timer-circle">
                    <svg className="timer-svg" viewBox="0 0 200 200">
                      <circle
                        className="timer-circle-bg"
                        cx="100"
                        cy="100"
                        r="90"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="10"
                      />
                      <circle
                        className="timer-circle-progress"
                        cx="100"
                        cy="100"
                        r="90"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="10"
                        strokeDasharray={`${2 * Math.PI * 90}`}
                        strokeDashoffset={`${2 * Math.PI * 90 * (1 - timeRemaining / currentStep?.duration)}`}
                        strokeLinecap="round"
                        transform="rotate(-90 100 100)"
                      />
                    </svg>
                    <div className="timer-text">
                      <span className="timer-value">{formatTime(timeRemaining)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {nextExercise && (
                <div className="rest-right-column">
                  <div className="next-exercise-preview">
                    <h3 className="next-exercise-label">{t('nextExercise') || 'Next Exercise'}:</h3>
                    <h4 className="next-exercise-name">{nextExercise.exercise?.name}</h4>
                    {nextExercise.currentSeries && nextExercise.series && (
                      <p className="next-exercise-series">
                        {t('series')} {nextExercise.currentSeries} / {nextExercise.series}
                      </p>
                    )}
                    {nextExercise.exercise?.description && (
                      <p className="next-exercise-description">{nextExercise.exercise.description}</p>
                    )}
                    {nextExercise.exercise?.imageUrl && (
                      <div className="next-exercise-image-container">
                        <img
                          src={nextExercise.exercise.imageUrl}
                          alt={nextExercise.exercise.name}
                          className="next-exercise-image"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="timer-controls">
            <Button variant="secondary" onClick={handleSkip} className="control-button">
              <SkipForward size={24} />
              {t('skip') || 'Skip'}
            </Button>
            <Button onClick={handlePlayPause} className="control-button-primary">
              {isRunning ? <Pause size={32} /> : <Play size={32} />}
              {isRunning ? t('pause') || 'Pause' : t('start') || 'Start'}
            </Button>
          </div>
        </Card>

        <Card className="progress-card">
          <h3 className="progress-title">{t('progress') || 'Progress'}</h3>
          <div className="steps-list">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`step-item ${index === currentStepIndex ? 'active' : ''} ${index < currentStepIndex ? 'completed' : ''} ${step.type}`}
              >
                <div className="step-marker" />
                <div className="step-content">
                  <span className="step-name">
                    {step.type === 'exercise' ? step.exercise?.name : t('rest')}
                  </span>
                  <span className="step-duration">{formatTime(step.duration)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};
